import { defineInvokeEventa } from "@moeru/eventa";
import { array, boolean, check, integer, literal, maxLength, maxValue, minLength, minValue, nullable, number, object, optional, pick, picklist, pipe, record, regex, safeParse, string, toLowerCase, union, unknown, uuid } from "valibot";

//#region src/operation.ts
/** Absolute deadline is preserved across CLI, IPC and HTTP hops. */
const readOperationFields = { operation: optional(object({ deadlineAt: pipe(number(), integer(), minValue(1)) })) };

//#endregion
//#region src/chats.ts
const chatTypeSchema = picklist([
	"user",
	"bot",
	"channel",
	"group",
	"supergroup"
]);
const listChatsInputSchema = object({
	...readOperationFields,
	cursor: optional(pipe(string(), regex(/^(?:0|[1-9]\d{0,15})$/), check((value) => Number.isSafeInteger(Number(value)), "Chat cursor must be a non-negative safe integer offset"))),
	limit: optional(pipe(number(), minValue(1), maxValue(1e3)), 100),
	types: optional(array(chatTypeSchema))
});
const chatRecordSchema = object({
	id: string(),
	name: string(),
	type: chatTypeSchema,
	username: optional(string()),
	lastMessage: optional(string()),
	lastMessageAt: optional(number())
});
const chatContracts = { list: defineInvokeEventa("tg.v1.chats.list") };

//#endregion
//#region src/daemon.ts
const daemonStateSchema = picklist([
	"starting",
	"ready",
	"reconnecting",
	"unauthorized",
	"error",
	"stopping"
]);
const healthTimes = {
	changedAt: number(),
	lastSuccessAt: optional(number())
};
const readHealthSchema = object({
	state: picklist([
		"unknown",
		"available",
		"failed"
	]),
	...healthTimes,
	lastFailureAt: optional(number()),
	errorCode: optional(string())
});
const daemonHealthSchema = object({
	transport: object({
		state: picklist([
			"starting",
			"connected",
			"disconnected",
			"broken"
		]),
		...healthTimes
	}),
	authorization: object({
		state: picklist([
			"unknown",
			"authorized",
			"unauthorized"
		]),
		...healthTimes
	}),
	catchUp: object({
		state: picklist([
			"unknown",
			"running",
			"idle",
			"failed"
		]),
		...healthTimes,
		error: optional(string())
	}),
	reads: object({
		telegram: readHealthSchema,
		local: readHealthSchema
	}),
	recovery: object({
		attempts: number(),
		inProgress: boolean(),
		lastAttemptAt: optional(number()),
		lastSuccessAt: optional(number())
	})
});
const runtimeBuildSchema = object({
	version: string(),
	artifact: string(),
	artifactDigest: string(),
	nodeVersion: string()
});
const daemonStatusSchema = object({
	health: daemonHealthSchema,
	build: runtimeBuildSchema,
	accountId: optional(string()),
	error: optional(string()),
	pid: number(),
	profile: string(),
	startedAt: number(),
	state: daemonStateSchema
});
const daemonContracts = {
	recover: defineInvokeEventa("tg.v1.daemon.recover"),
	reload: defineInvokeEventa("tg.v1.daemon.reload"),
	status: defineInvokeEventa("tg.v1.daemon.status"),
	stop: defineInvokeEventa("tg.v1.daemon.stop")
};

//#endregion
//#region src/errors.ts
const appErrorSchema = object({
	code: string(),
	message: string(),
	retryable: boolean(),
	retryAfterSeconds: optional(number()),
	details: optional(record(string(), unknown()))
});
function ok(data) {
	return {
		ok: true,
		data
	};
}
function err(error) {
	return {
		ok: false,
		error
	};
}

//#endregion
//#region src/export.ts
const exportInputSchema = object({
	outputDir: string(),
	format: optional(literal("jsonl"), "jsonl"),
	timeZone: optional(string(), "UTC"),
	chatIds: optional(array(string())),
	from: optional(number()),
	to: optional(number())
});
const exportContracts = { run: defineInvokeEventa("tg.v1.export.run") };

//#endregion
//#region src/media.ts
const MEDIA_CHUNK_BYTES = 256 * 1024;
const readMessageMediaInputSchema = object({
	...readOperationFields,
	source: picklist(["local", "telegram"]),
	chatId: pipe(string(), minLength(1)),
	messageId: pipe(string(), regex(/^[1-9]\d{0,15}$/), check((value) => Number.isSafeInteger(Number(value)), "Expected a safe message ID")),
	mediaIndex: optional(pipe(number(), integer(), minValue(0), maxValue(Number.MAX_SAFE_INTEGER)), 0),
	offset: optional(pipe(number(), integer(), minValue(0), maxValue(Number.MAX_SAFE_INTEGER)), 0),
	length: optional(pipe(number(), integer(), minValue(1), maxValue(MEDIA_CHUNK_BYTES)), 64 * 1024),
	maxBytes: optional(pipe(number(), integer(), minValue(1), maxValue(50 * 1024 * 1024)), 10 * 1024 * 1024)
});
const messageMediaChunkSchema = object({
	source: picklist(["local", "telegram"]),
	chatId: string(),
	messageId: string(),
	mediaIndex: number(),
	type: string(),
	fileName: optional(string()),
	mimeType: optional(string()),
	size: number(),
	encoding: literal("base64"),
	data: string(),
	offset: number(),
	nextOffset: nullable(number()),
	contentStatus: picklist(["partial", "complete"])
});
const createMediaLinkInputSchema = object({
	...pick(readMessageMediaInputSchema, [
		"operation",
		"source",
		"chatId",
		"messageId",
		"mediaIndex",
		"maxBytes"
	]).entries,
	chatId: pipe(string(), minLength(1), maxLength(128))
});
const mediaDownloadLinkSchema = object({
	path: pipe(string(), maxLength(4096), regex(/^\/v1\/remote\/media\.download\?ticket=[\w-]+$/)),
	expiresAt: pipe(number(), integer(), minValue(1), maxValue(Number.MAX_SAFE_INTEGER)),
	media: object({
		type: pipe(string(), minLength(1)),
		size: pipe(number(), integer(), minValue(0), maxValue(50 * 1024 * 1024)),
		mimeType: optional(string()),
		fileName: optional(string())
	})
});
const mediaContracts = { read: defineInvokeEventa("tg.v1.media.read") };

//#endregion
//#region src/messages.ts
const timeRangeFields = {
	from: optional(number()),
	to: optional(number())
};
const localMessageCursorSchema = pipe(string(), regex(/^(?:0|[1-9]\d{0,15})$/), check((value) => Number.isSafeInteger(Number(value)), "Local cursor must be a non-negative safe integer offset"));
const telegramMessageIdSchema = pipe(string(), regex(/^[1-9]\d{0,15}$/), check((value) => Number.isSafeInteger(Number(value)), "Telegram message ID must be a positive safe integer"));
const listRemoteMessagesInputSchema = object({
	...readOperationFields,
	chatId: pipe(string(), minLength(1), maxLength(128)),
	cursor: optional(telegramMessageIdSchema),
	limit: optional(pipe(number(), minValue(1), maxValue(1e3)), 100),
	query: optional(pipe(string(), minLength(1), maxLength(4e3))),
	fromUserId: optional(string()),
	minMessageId: optional(pipe(number(), minValue(0))),
	...timeRangeFields
});
const queryLocalMessagesInputSchema = object({
	...readOperationFields,
	chatIds: optional(array(pipe(string(), minLength(1), maxLength(128)))),
	cursor: optional(localMessageCursorSchema),
	limit: optional(pipe(number(), minValue(1), maxValue(1e3)), 100),
	fromUserId: optional(string()),
	...timeRangeFields
});
const searchMessagesInputSchema = object({
	...readOperationFields,
	query: pipe(string(), minLength(1), maxLength(4e3)),
	chatIds: optional(array(pipe(string(), minLength(1), maxLength(128)))),
	cursor: optional(localMessageCursorSchema),
	limit: optional(pipe(number(), minValue(1), maxValue(1e3)), 100),
	useVector: optional(boolean(), false),
	...timeRangeFields
});
const messageContextInputSchema = object({
	...readOperationFields,
	chatId: pipe(string(), minLength(1), maxLength(128)),
	messageId: pipe(string(), minLength(1), maxLength(128)),
	before: optional(pipe(number(), minValue(0), maxValue(1e3)), 20),
	after: optional(pipe(number(), minValue(0), maxValue(1e3)), 20)
});
const messageForwardRecordSchema = object({
	isForward: boolean(),
	fromChatId: optional(string()),
	fromChatName: optional(string()),
	fromMessageId: optional(string()),
	fromSenderId: optional(string()),
	fromName: optional(string()),
	date: optional(number()),
	postAuthor: optional(string())
});
const messageMediaRecordSchema = object({
	type: string(),
	fileName: optional(string()),
	mimeType: optional(string()),
	telegramReference: optional(string())
});
const messageLinkRecordSchema = object({
	url: string(),
	title: optional(string())
});
const messageQuoteSchema = object({
	text: string(),
	offset: optional(number()),
	manual: optional(boolean())
});
/** Canonical wire schema: all transports preserve the same message facts. */
const messageRecordSchema = object({
	id: string(),
	chatId: string(),
	senderId: string(),
	senderName: string(),
	timestamp: number(),
	text: string(),
	replyToId: optional(string()),
	replyToChatId: optional(string()),
	replyToTopId: optional(string()),
	forumTopic: optional(boolean()),
	quote: optional(messageQuoteSchema),
	groupedId: optional(string()),
	forward: messageForwardRecordSchema,
	media: array(messageMediaRecordSchema),
	links: array(messageLinkRecordSchema),
	editedAt: optional(number()),
	deletedAt: optional(number())
});
const getMessagesInputSchema = object({
	...readOperationFields,
	source: picklist(["local", "telegram"]),
	chatId: pipe(string(), minLength(1), maxLength(128)),
	messageIds: pipe(array(telegramMessageIdSchema), minLength(1), maxLength(100))
});
const getMessagesResultSchema = object({
	source: picklist(["local", "telegram"]),
	items: array(messageRecordSchema),
	missingIds: array(string())
});
const referenceSchema = object({
	chatId: string(),
	messageId: string()
});
const replyStatusSchema = picklist([
	"resolved",
	"missing",
	"not_requested",
	"depth_budget",
	"node_budget",
	"cycle"
]);
const replyContextInputSchema = object({
	...getMessagesInputSchema.entries,
	maxDepth: optional(pipe(number(), integer(), minValue(0), maxValue(10)), 1),
	maxNodes: optional(pipe(number(), integer(), minValue(1), maxValue(1e3)), 100)
});
const replyContextResultSchema = object({
	source: picklist(["local", "telegram"]),
	items: array(messageRecordSchema),
	roots: array(object({
		...referenceSchema.entries,
		status: picklist([
			"resolved",
			"missing",
			"node_budget"
		])
	})),
	links: array(object({
		from: referenceSchema,
		to: referenceSchema,
		status: replyStatusSchema,
		reason: optional(picklist(["cross_chat", "depth_zero"]))
	})),
	budget: object({
		maxDepth: number(),
		maxNodes: number()
	}),
	coverage: object({
		complete: boolean(),
		resolvedReplies: number(),
		missingReplies: number(),
		notRequestedReplies: number(),
		depthStopped: number(),
		nodeStopped: number(),
		cycles: number()
	})
});
const readMessagesInputSchema = pipe(object({
	...readOperationFields,
	source: picklist(["local", "telegram"]),
	chatId: pipe(string(), minLength(1), maxLength(128)),
	cursor: optional(string()),
	pageSize: optional(pipe(number(), integer(), minValue(1), maxValue(1e3)), 100),
	maxPages: optional(pipe(number(), integer(), minValue(1), maxValue(100)), 10),
	maxMessages: optional(pipe(number(), integer(), minValue(1), maxValue(1e4)), 1e3),
	...timeRangeFields
}), check((input) => input.from === void 0 || input.to === void 0 || input.from <= input.to, "from must be before or equal to to"), check((input) => input.cursor === void 0 || safeParse(input.source === "telegram" ? telegramMessageIdSchema : localMessageCursorSchema, input.cursor).success, "Cursor must match the selected source"));
const readMessagesResultSchema = object({
	source: picklist(["local", "telegram"]),
	chatId: string(),
	items: array(messageRecordSchema),
	window: object(timeRangeFields),
	nextCursor: nullable(string()),
	pagesRead: number(),
	stopReason: picklist([
		"exhausted",
		"page_budget",
		"message_budget",
		"cursor_stalled"
	]),
	coverage: object({
		windowExhausted: boolean(),
		replyContext: literal("not_requested"),
		mediaContent: literal("not_requested")
	})
});
const messageContracts = {
	replies: defineInvokeEventa("tg.v1.messages.replies"),
	read: defineInvokeEventa("tg.v1.messages.read"),
	get: defineInvokeEventa("tg.v1.messages.get"),
	listRemote: defineInvokeEventa("tg.v1.messages.list.remote"),
	queryLocal: defineInvokeEventa("tg.v1.messages.query.local"),
	searchLocal: defineInvokeEventa("tg.v1.messages.search.local"),
	contextLocal: defineInvokeEventa("tg.v1.messages.context.local")
};

//#endregion
//#region src/pagination.ts
const cursorInputSchema = object({
	cursor: optional(string()),
	limit: optional(pipe(number(), minValue(1), maxValue(1e3)), 100)
});

//#endregion
//#region src/stats.ts
const statsInputSchema = object({
	...readOperationFields,
	groupBy: optional(picklist([
		"month",
		"chat",
		"sender"
	]), "month"),
	timeZone: optional(string(), "UTC"),
	chatIds: optional(array(string())),
	from: optional(number()),
	to: optional(number())
});
const statsContracts = { get: defineInvokeEventa("tg.v1.stats.get") };

//#endregion
//#region src/remote.ts
const messagePageSchema = object({
	items: array(object({
		...messageRecordSchema.entries,
		similarity: optional(number()),
		combinedScore: optional(number())
	})),
	nextCursor: nullable(string()),
	total: optional(number())
});
const accountIdSchema = pipe(string("Expected a database account UUID"), uuid("Expected a database account UUID"), toLowerCase());
const remoteMethods = {
	"media.createLink": {
		input: createMediaLinkInputSchema,
		output: mediaDownloadLinkSchema
	},
	"media.read": {
		input: readMessageMediaInputSchema,
		output: messageMediaChunkSchema
	},
	"chats.list": {
		input: listChatsInputSchema,
		output: object({
			items: array(chatRecordSchema),
			nextCursor: nullable(string()),
			total: optional(number())
		})
	},
	"messages.replies": {
		input: replyContextInputSchema,
		output: replyContextResultSchema
	},
	"messages.read": {
		input: readMessagesInputSchema,
		output: readMessagesResultSchema
	},
	"messages.get": {
		input: getMessagesInputSchema,
		output: getMessagesResultSchema
	},
	"messages.list": {
		input: listRemoteMessagesInputSchema,
		output: messagePageSchema
	},
	"messages.query": {
		input: queryLocalMessagesInputSchema,
		output: messagePageSchema
	},
	"messages.search": {
		input: searchMessagesInputSchema,
		output: messagePageSchema
	},
	"messages.context": {
		input: messageContextInputSchema,
		output: object({
			messages: array(object({
				...messageRecordSchema.entries,
				similarity: optional(number()),
				combinedScore: optional(number())
			})),
			targetIndex: number()
		})
	},
	"stats.get": {
		input: statsInputSchema,
		output: object({
			total: number(),
			buckets: array(object({
				key: string(),
				count: number(),
				firstTimestamp: number(),
				lastTimestamp: number()
			}))
		})
	}
};
function remoteResultSchema(output) {
	return union([object({
		ok: literal(true),
		data: output
	}), object({
		ok: literal(false),
		error: appErrorSchema
	})]);
}
function normalizeRemoteUrl(value) {
	const url = new URL(value);
	if (url.username || url.password || url.search || url.hash) throw new Error("Remote URL must not contain credentials, query parameters, or a fragment");
	const loopback = [
		"localhost",
		"127.0.0.1",
		"[::1]"
	].includes(url.hostname);
	if (url.protocol !== "https:" && (url.protocol !== "http:" || !loopback)) throw new Error("Remote URL requires HTTPS (HTTP is allowed only on loopback)");
	return url.href.replace(/\/$/, "");
}

//#endregion
//#region src/sync.ts
const syncInputSchema = object({
	chatIds: optional(array(string()), []),
	all: optional(boolean(), false),
	takeout: optional(boolean(), false),
	from: optional(number()),
	to: optional(number()),
	limit: optional(pipe(number(), minValue(1), maxValue(1e6)), 1e5)
});
const syncContracts = { run: defineInvokeEventa("tg.v1.sync.run") };

//#endregion
export { MEDIA_CHUNK_BYTES, accountIdSchema, appErrorSchema, chatContracts, chatRecordSchema, chatTypeSchema, createMediaLinkInputSchema, cursorInputSchema, daemonContracts, daemonHealthSchema, daemonStateSchema, daemonStatusSchema, err, exportContracts, exportInputSchema, getMessagesInputSchema, getMessagesResultSchema, listChatsInputSchema, listRemoteMessagesInputSchema, localMessageCursorSchema, mediaContracts, mediaDownloadLinkSchema, messageContextInputSchema, messageContracts, messageForwardRecordSchema, messageLinkRecordSchema, messageMediaChunkSchema, messageMediaRecordSchema, messageQuoteSchema, messageRecordSchema, normalizeRemoteUrl, ok, queryLocalMessagesInputSchema, readMessageMediaInputSchema, readMessagesInputSchema, readMessagesResultSchema, readOperationFields, remoteMethods, remoteResultSchema, replyContextInputSchema, replyContextResultSchema, runtimeBuildSchema, searchMessagesInputSchema, statsContracts, statsInputSchema, syncContracts, syncInputSchema };
//# sourceMappingURL=index.mjs.map